import { getDataClient } from "@/lib/dataClient";
import { apiRequest } from "@/lib/api";
import { useRef, useState } from "react";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

export interface ChatMessage {
  id: string;
  sender: string;
  text: string;
  time: string;
  isMe: boolean;
}

export interface LiveClass {
  id: string;
  title: string;
  subject: string;
  grade: string;
  teacher: string;
  teacherAvatar: string;
  date: string;
  time: string;
  duration: number;
  status: "planned" | "live" | "ended" | "cancelled";
  participants: number;
  maxParticipants: number;
  description: string;
  hasRecording: boolean;
  recordingUrl: string;
  roomCode: string;
  teacherId: string;
  studentIds?: string[];
  chatMessages?: ChatMessage[];
}

export interface Conversation {
  id: string;
  peerId?: string;
  name: string;
  role: string;
  avatar: string;
  lastMessage: string;
  time: string;
  unread: number;
  messages: ChatMessage[];
}

function fmtDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return { date: `${y}.${m}.${day}`, time: `${h}:${min}` };
}

export function useLiveClasses() {
  return useDataQuery<LiveClass[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("live_classes")
      .select("*")
      .order("starts_at", { ascending: false });
    if (error) throw error;

    const all = (data ?? []).map((lc: { id: string; subject: string; title: string; teacher_id: string | null; teacher_name: string | null; teacher_avatar: string | null; grade: string | null; starts_at: string; duration_minutes: number | null; status: string; participants: number | null; max_participants: number | null; description: string | null; has_recording: boolean; room_code: string | null; student_ids: string[] | null; recording_url: string | null }) => {
      const dt = fmtDateTime(lc.starts_at);
      return {
        id: lc.id,
        title: lc.title,
        subject: lc.subject,
        grade: lc.grade ?? "",
        teacher: lc.teacher_name ?? "",
        teacherAvatar: lc.teacher_avatar ?? "",
        date: dt.date,
        time: dt.time,
        duration: lc.duration_minutes ?? 0,
        status: lc.status as LiveClass["status"],
        participants: lc.participants ?? 0,
        maxParticipants: lc.max_participants ?? 0,
        description: lc.description ?? "",
        hasRecording: lc.has_recording ?? false,
        recordingUrl: lc.recording_url ?? "",
        roomCode: lc.room_code ?? "",
        teacherId: lc.teacher_id ?? "",
        studentIds: lc.student_ids ?? [],
      };
    });

    // The API applies the authenticated account's teacher/student/parent scope.
    return all;
  });
}

export function useMessages() {
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const pending = useRef<{ key: string; id: string } | null>(null);
  const query = useDataQuery<Conversation[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];

    // Parent messages belong to the parent, not the linked child's data scope.
    // The shared endpoint enforces the current account as owner for all roles.
    async function allRows(table: string) {
      const rows: Record<string, unknown>[] = [];
      let more = true;
      while (more) {
        const page = await apiRequest<{ data: Record<string, unknown>[]; has_more: boolean }>(`/mobile/records?table=${table}&offset=${rows.length}`);
        rows.push(...page.data); more = page.has_more && page.data.length > 0;
      }
      return rows;
    }
    const [conversations, messages] = await Promise.all([allRows("conversations"), allRows("messages")]);

    const msgByConv = new Map<string, ChatMessage[]>();
    messages.reverse().forEach((raw) => {
      const m = raw as unknown as { id: string; conversation_id: string; sender: string; text: string; time: string | null; is_me: boolean };
      const list = msgByConv.get(m.conversation_id) ?? [];
      list.push({ id: m.id, sender: m.sender, text: m.text, time: m.time ?? "", isMe: m.is_me ?? false });
      msgByConv.set(m.conversation_id, list);
    });

    return conversations.map((raw) => {
      const c = raw as unknown as { id: string; peer_id?: string; name: string; role: string | null; avatar: string | null; last_message: string | null; last_time: string | null; unread: number };
      return ({
      id: c.id,
      peerId: c.peer_id,
      name: c.name,
      role: c.role ?? "",
      avatar: c.avatar ?? "",
      lastMessage: c.last_message ?? "",
      time: c.last_time ?? "",
      unread: c.unread ?? 0,
      messages: msgByConv.get(c.id) ?? [],
      });
    });
  });

  const sendMessage = async (conversationId: string, text: string) => {
    if (inFlight.current) return false;
    const peer = query.data?.find((c) => c.id === conversationId)?.peerId;
    if (!peer) { setSendError("Bu eski görüşmenin alıcı bağlantısı yok. Mobil uygulamadan kişi seçerek yeni görüşme başlatın."); return false; }
    const key = `${peer}:${text}`;
    if (pending.current?.key !== key) pending.current = { key, id: crypto.randomUUID() };
    inFlight.current = true; setSending(true); setSendError(null);
    try {
      await apiRequest("/mobile/message", { method: "POST", body: { peer_id: peer, text, request_id: pending.current.id } });
      pending.current = null; query.reload(); return true;
    } catch (error) {
      setSendError(error instanceof Error ? error.message : "Mesaj gönderilemedi."); return false;
    } finally { inFlight.current = false; setSending(false); }
  };

  const markRead = async (conversationId: string) => {
    try {
      await apiRequest("/mobile/action", { method: "POST", body: { table: "conversations", id: conversationId, action: "read" } });
      query.reload();
    } catch (error) { setSendError(error instanceof Error ? error.message : "Görüşme güncellenemedi."); }
  };

  return { ...query, sendMessage, markRead, sending, sendError };
}
